import { useEffect, useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { API } from "@/App";
import logger from "@/utils/logger";
import { toast } from "sonner";
import adminApi from "./adminApi";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { CalendarOff, CalendarCheck, Trash2, Plus } from "lucide-react";
import { format } from "date-fns";

const AvailabilityManagement = () => {
  const { t } = useTranslation();
  const [blockedDates, setBlockedDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [reason, setReason] = useState("");
  const [blocking, setBlocking] = useState(false);

  const fetchBlockedDates = useCallback(async () => {
    try {
      const response = await adminApi.get(`${API}/availability/blocked-dates`);
      setBlockedDates(response.data);
    } catch (error) {
      logger.error("Error fetching blocked dates:", error);
    } finally {
      setLoading(false);
    }
  }, [API, adminApi]);

  useEffect(() => {
    fetchBlockedDates();
  }, [fetchBlockedDates]);

  const blockedDateStrings = useMemo(() => new Set(blockedDates.map((b) => b.date)), [blockedDates]);
  const manualDates = useMemo(() => new Set(blockedDates.filter((b) => b.source === "manual").map((b) => b.date)), [blockedDates]);
  const bookingDates = useMemo(() => new Set(blockedDates.filter((b) => b.source === "booking").map((b) => b.date)), [blockedDates]);

  const handleBlockDate = async () => {
    if (!selectedDate) {
      toast.error("Please select a date first.");
      return;
    }
    const dateStr = format(selectedDate, "yyyy-MM-dd");
    if (blockedDateStrings.has(dateStr)) {
      toast.error("This date is already blocked.");
      return;
    }
    setBlocking(true);
    try {
      await adminApi.post(`${API}/availability/block-date`, {
        date: dateStr,
        reason: reason || "Manually blocked",
      });
      toast.success(`${format(selectedDate, "MMMM d, yyyy")} has been blocked.`);
      setSelectedDate(null);
      setReason("");
      fetchBlockedDates();
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to block date.";
      toast.error(msg);
    } finally {
      setBlocking(false);
    }
  };

  const handleUnblockDate = async (dateStr) => {
    if (!window.confirm(`Unblock ${dateStr}?`)) return;
    try {
      await adminApi.delete(`${API}/availability/block-date/${dateStr}`);
      toast.success(`${dateStr} has been unblocked.`);
      fetchBlockedDates();
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to unblock date.";
      toast.error(msg);
    }
  };

  const calendarModifiers = useMemo(() => ({
    blocked_manual: (d) => manualDates.has(format(d, "yyyy-MM-dd")),
    blocked_booking: (d) => bookingDates.has(format(d, "yyyy-MM-dd")),
  }), [manualDates, bookingDates]);

  const calendarModifiersStyles = useMemo(() => ({
    blocked_manual: {
      backgroundColor: "#E07A5F",
      color: "white",
      borderRadius: "8px",
    },
    blocked_booking: {
      backgroundColor: "#3D405B",
      color: "white",
      borderRadius: "8px",
    },
  }), []);

  const sortedBlockedDates = useMemo(() => [...blockedDates].sort((a, b) => a.date.localeCompare(b.date)), [blockedDates]);

  return (
    <div data-testid="availability-management" className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-[#3D405B] font-['Playfair_Display']">
          Date Availability
        </h1>
        <p className="text-[#6B705C]">
          Manage which dates are available for bookings. Confirmed bookings auto-block dates.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Calendar + Block Form */}
        <Card className="bg-white border-0 shadow-sm" data-testid="availability-calendar-card">
          <CardContent className="p-6 space-y-4">
            <h3 className="font-semibold text-[#3D405B] text-lg">Block a Date</h3>
            <p className="text-sm text-[#6B705C]">
              Select a date on the calendar below and click &ldquo;Block Date&rdquo; to make it unavailable.
            </p>
            <div className="flex justify-center">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                disabled={(d) => d < new Date()}
                modifiers={calendarModifiers}
                modifiersStyles={calendarModifiersStyles}
                data-testid="admin-availability-calendar"
              />
            </div>

            <div className="flex items-center gap-4 text-xs text-[#6B705C]">
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded bg-[#E07A5F]" />
                <span>Manually blocked</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded bg-[#3D405B]" />
                <span>Confirmed booking</span>
              </div>
            </div>

            {selectedDate && !blockedDateStrings.has(format(selectedDate, "yyyy-MM-dd")) && (
              <div className="border-t pt-4 space-y-3" data-testid="block-date-form">
                <p className="text-sm font-medium text-[#3D405B]">
                  Block: <strong>{format(selectedDate, "MMMM d, yyyy")}</strong>
                </p>
                <Input
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Reason (e.g. Holiday, Vacation, Personal)"
                  data-testid="block-reason-input"
                  className="bg-[#F4F1DE]/50"
                />
                <Button
                  onClick={handleBlockDate}
                  disabled={blocking}
                  data-testid="confirm-block-btn"
                  className="w-full bg-[#E07A5F] hover:bg-[#D0694E] text-white rounded-full"
                >
                  {blocking ? "Blocking..." : (
                    <>
                      <CalendarOff className="w-4 h-4 mr-2" />
                      Block Date
                    </>
                  )}
                </Button>
              </div>
            )}

            {selectedDate && blockedDateStrings.has(format(selectedDate, "yyyy-MM-dd")) && (
              <div className="border-t pt-4">
                <p className="text-sm text-[#E07A5F] font-medium">
                  This date is already blocked.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Blocked Dates List */}
        <Card className="bg-white border-0 shadow-sm" data-testid="blocked-dates-list-card">
          <CardContent className="p-6 space-y-4">
            <h3 className="font-semibold text-[#3D405B] text-lg">Blocked Dates</h3>

            {loading && (
              <div className="flex justify-center py-8">
                <div className="spinner"></div>
              </div>
            )}

            {!loading && sortedBlockedDates.length === 0 && (
              <div className="text-center py-8">
                <CalendarCheck className="w-12 h-12 text-[#2D6A4F] mx-auto mb-3" />
                <p className="text-[#6B705C]">All dates are currently available!</p>
              </div>
            )}

            {!loading && sortedBlockedDates.length > 0 && (
              <div className="space-y-3 max-h-[500px] overflow-y-auto">
                {sortedBlockedDates.map((blocked) => (
                  <div
                    key={blocked.id}
                    className="flex items-center justify-between p-3 bg-[#F4F1DE]/50 rounded-lg"
                    data-testid={`blocked-date-${blocked.date}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-8 rounded-full ${blocked.source === "manual" ? "bg-[#E07A5F]" : "bg-[#3D405B]"}`} />
                      <div>
                        <p className="font-medium text-[#3D405B] text-sm">{blocked.date}</p>
                        <p className="text-xs text-[#6B705C]">{blocked.reason}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={blocked.source === "manual" ? "bg-[#E07A5F]/10 text-[#E07A5F]" : "bg-[#3D405B]/10 text-[#3D405B]"}>
                        {blocked.source === "manual" ? "Manual" : "Booking"}
                      </Badge>
                      {blocked.source === "manual" && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleUnblockDate(blocked.date)}
                          className="text-red-600 hover:text-red-700 h-8 w-8 p-0"
                          data-testid={`unblock-${blocked.date}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AvailabilityManagement;
